"use client";

import Link from "next/link";
import { FaLinkedin, FaFacebook, FaWhatsapp, FaEnvelope, FaPhone, FaMapMarkerAlt } from "react-icons/fa";
import Image from "next/image";
import { site, trackContact } from "../../lib/site";

export default function Footer() {
  return (
    <footer className="bg-slate-50 border-t border-slate-200 pb-20 lg:pb-0">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-[minmax(0,1.4fr)_1fr_1fr_1fr]">
          {/* Company Info */}
          <div className="space-y-4">
            <Link href="/" className="inline-flex items-center gap-2 whitespace-nowrap">
              <Image src="/static/img/logo.png" alt={site.name} width={140} height={140} className="h-10 w-auto shrink-0" />
              <span className="whitespace-nowrap text-base font-bold text-brand-900">{site.name}</span>
            </Link>
            <p className="text-gray-600 text-sm">ERP, SAP Business One, CRM, and logistics software for businesses across Africa and India.</p><p className="text-xs font-semibold uppercase tracking-wider text-brand-700">{site.marketsLabel}</p>
            <div className="flex space-x-4">
              <Link href={site.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LITPL on LinkedIn" className="text-gray-400 hover:text-brand-600 transition-colors">
                <FaLinkedin className="w-5 h-5" />
              </Link>
              <Link href={site.facebook} target="_blank" rel="noopener noreferrer" aria-label="LITPL on Facebook" className="text-gray-400 hover:text-brand-600 transition-colors">
                <FaFacebook className="w-5 h-5" />
              </Link>
              <Link href={`https://wa.me/${site.whatsappNumber}`} onClick={() => trackContact("whatsapp")} target="_blank" rel="noopener noreferrer" aria-label="Chat with LITPL on WhatsApp" className="text-gray-400 hover:text-brand-600 transition-colors">
                <FaWhatsapp className="w-5 h-5" />
              </Link>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/about" className="text-gray-600 hover:text-brand-600 transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link href="/where-we-work" className="text-gray-600 hover:text-brand-600 transition-colors">
                  Where we work
                </Link>
              </li>
              <li>
                <Link href="/services" className="text-gray-600 hover:text-brand-600 transition-colors">
                  Services
                </Link>
              </li>
              <li>
                <Link href="/industries" className="text-gray-600 hover:text-brand-600 transition-colors">
                  Industries
                </Link>
              </li>
              <li>
                <Link href="/guides" className="text-gray-600 hover:text-brand-600 transition-colors">
                  Guides
                </Link>
              </li>
              <li>
                <Link href="/blog" className="text-gray-600 hover:text-brand-600 transition-colors">
                  Blog
                </Link>
              </li>
              <li>
                <Link href="/contact" className="text-gray-600 hover:text-brand-600 transition-colors">
                  Contact
                </Link>
              </li>
              <li>
                <Link href="/careers" className="text-gray-600 hover:text-brand-600 transition-colors">
                  Careers
                </Link>
              </li>
              <li>
                <Link href="/training" className="text-gray-600 hover:text-brand-600 transition-colors">
                  Training
                </Link>
              </li>
              <li>
                <Link href="/codecrush-daily" className="text-gray-600 hover:text-brand-600 transition-colors">
                  CodeCrush Daily
                </Link>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Products</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/products/sap" className="text-gray-600 hover:text-brand-600 transition-colors">
                  SAP Business One
                </Link>
              </li>
              <li>
                <Link href="/products/sap-s4-public-cloud" className="text-gray-600 hover:text-brand-600 transition-colors">
                  SAP S/4HANA Public Cloud
                </Link>
              </li>
              <li>
                <Link href="/products/lit-fusion" className="text-gray-600 hover:text-brand-600 transition-colors">
                  LITFusion
                </Link>
              </li>
              <li>
                <Link href="/products/lit-crm" className="text-gray-600 hover:text-brand-600 transition-colors">
                  LITCRM
                </Link>
              </li>
              <li>
                <Link href="/products/lit-khata" className="text-gray-600 hover:text-brand-600 transition-colors">
                  LITKHATA
                </Link>
              </li>
              <li>
                <Link href="/products/lit-way" className="text-gray-600 hover:text-brand-600 transition-colors">
                  LITWay
                </Link>
              </li>
              <li>
                <Link href="/products/icd" className="text-gray-600 hover:text-brand-600 transition-colors">
                  ICD Management
                </Link>
              </li>
              <li>
                <Link href="/products" className="text-gray-600 hover:text-brand-600 transition-colors">
                  All Products
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <FaMapMarkerAlt className="text-brand-600 mt-1" />
                <span className="text-gray-600">Head office: {site.address.addressLocality}, {site.address.addressRegion} {site.address.postalCode}, India<br /><span className="text-xs text-slate-500">Clients across Africa and India</span></span>
              </li>
              <li className="flex items-start space-x-3">
                <FaPhone className="text-brand-600 mt-1" />
                <a href={`tel:${site.phoneTel}`} onClick={() => trackContact("call")} className="text-gray-600 hover:text-brand-600 transition-colors">
                  {site.phoneDisplay}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <FaEnvelope className="text-brand-600 mt-1" />
                <a href={`mailto:${site.email}`} onClick={() => trackContact("email")} className="text-gray-600 hover:text-brand-600 transition-colors">
                  {site.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-100 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-600 text-sm">© {new Date().getFullYear()} {site.name}. All rights reserved.</p>
            <div className="flex space-x-6">
              <Link href="/privacy-policy" className="text-gray-600 hover:text-brand-600 transition-colors text-sm">
                Privacy Policy
              </Link>
              <Link href="/terms" className="text-gray-600 hover:text-brand-600 transition-colors text-sm">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

